import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/flexkit/Button";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useListGroups } from "@/queries/group-queries";
import { useListGroupMemberships, useDeleteGroupMembership } from "@/queries/group-queries";
import { useMembers } from "@/queries/organization-queries";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { MoreHorizontal } from "lucide-react";
import { IconUserAdd } from "@/assets/icons/geist/IconUserAdd";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { AddGroupMembersDialog } from "@/components/settings/organization/groups/AddGroupMembersDialog";
import { DeleteGroupDialog } from "@/components/settings/organization/groups/DeleteGroupDialog";
import { EditGroupDialog } from "@/components/settings/organization/groups/EditGroupDialog";
import { useState, useMemo, useCallback, type FC, useContext, useEffect } from "react";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { RemoveMemberConfirmDialog } from "@/components/settings/organization/groups/RemoveMemberConfirmDialog";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { cn } from "@/components/podkit/lib/cn";
import { GroupNotFound } from "./GroupNotFound";
import { GroupIsSystemManagedWarning } from "./GroupIsSystemManagedWarning";
import { GroupMembersTable } from "./GroupMembersTable";

export const GroupDetailsPage: FC = () => {
    const { setCustomBreadcrumbs, setBreadCrumbRowAction } = useContext(SettingsPageContext);
    const { groupId } = useParams<{ groupId: string }>();
    const navigate = useNavigate();
    const { data: groupsData, isPending: isLoadingGroups } = useListGroups();
    const { data: memberships, isPending: isGroupMembershipsLoading } = useListGroupMemberships(groupId || "");
    const { data: orgMembers, isPending: isLoadingOrgMembers } = useMembers();
    const { hasPermission } = useResourcePermissions();
    const { toast } = useToast();
    const deleteMembership = useDeleteGroupMembership();
    const [showAddMembersDialog, setShowAddMembersDialog] = useState(false);
    const [membersToRemove, setMembersToRemove] = useState<string[]>([]);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);
    const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);

    // Find the group from the groups list
    const group = useMemo(() => {
        return groupsData?.groups.find((g) => g.id === groupId);
    }, [groupsData?.groups, groupId]);

    const canAddMembers = group ? hasPermission(ResourceType.GROUP, group.id, "groupmembership:create") : false;
    const canEdit = group ? hasPermission(ResourceType.GROUP, group.id, "group:update") : false;
    const canDelete = group ? hasPermission(ResourceType.GROUP, group.id, "group:delete") : false;
    const canModifyMembers = group ? hasPermission(ResourceType.GROUP, group.id, "groupmembership:delete") : false;

    useEffect(() => {
        if (!group) {
            setCustomBreadcrumbs(null);
            setBreadCrumbRowAction(null);
            return;
        }

        setCustomBreadcrumbs([{ label: "Groups", to: "/settings/members#groups" }, { label: group.name }]);

        // Set header actions (swapped order: ... menu first, then Add People)
        setBreadCrumbRowAction(
            <div className="flex items-start gap-2">
                <DropdownActions
                    button={
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn("size-8 p-0", {
                                hidden: !canEdit && !canDelete,
                            })}
                        >
                            <MoreHorizontal className="size-4" />
                        </Button>
                    }
                    align="end"
                >
                    <DropdownMenuItem
                        data-tracking-id="group-edit"
                        className={cn({
                            hidden: !canEdit,
                        })}
                        onClick={() => setShowEditGroupDialog(true)}
                    >
                        <IconEdit size="sm" className="mr-2" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        variant="destructive"
                        data-tracking-id="group-delete"
                        onClick={() => setShowDeleteGroupDialog(true)}
                        className={cn({
                            hidden: !canDelete,
                        })}
                    >
                        <IconTrash size="sm" className="mr-2" />
                        Delete
                    </DropdownMenuItem>
                </DropdownActions>

                <Button
                    variant="primary"
                    size="sm"
                    LeadingIcon={IconUserAdd}
                    onClick={() => setShowAddMembersDialog(true)}
                    data-tracking-id="group-add-people"
                    className={cn({
                        hidden: !canAddMembers,
                    })}
                >
                    Add People
                </Button>
            </div>,
        );

        // Clean up when unmounting
        return () => {
            setCustomBreadcrumbs(null);
            setBreadCrumbRowAction(null);
        };
    }, [group, canEdit, canDelete, canAddMembers, setCustomBreadcrumbs, setBreadCrumbRowAction]);

    const handleRemoveMembers = useCallback((memberIds: string[]) => {
        setMembersToRemove(memberIds);
        setShowRemoveConfirmation(true);
    }, []);

    const handleConfirmRemove = useCallback(async () => {
        if (membersToRemove.length === 0) {
            setShowRemoveConfirmation(false);
            return;
        }

        // Process removals sequentially to avoid race conditions
        let hasError = false;
        const isMultiple = membersToRemove.length > 1;

        for (const memberId of membersToRemove) {
            const membership = memberships?.find((m) => m.subject?.id === memberId);
            if (!membership?.id || !groupId) {
                hasError = true;
                continue;
            }

            try {
                await deleteMembership.mutateAsync({
                    membershipId: membership.id,
                    groupId,
                });
            } catch {
                hasError = true;
            }
        }

        if (!hasError) {
            toast({
                title: isMultiple ? "Members removed" : "Member removed",
                description: isMultiple
                    ? `Successfully removed ${membersToRemove.length} members from the group.`
                    : "Member has been successfully removed from the group.",
            });
        } else {
            toast({
                title: isMultiple ? "Partial removal" : "Failed to remove member",
                description: isMultiple
                    ? "Some members could not be removed from the group."
                    : "An error occurred while removing the member from the group.",
            });
        }

        // Clear state
        setMembersToRemove([]);
        setShowRemoveConfirmation(false);
    }, [membersToRemove, memberships, groupId, deleteMembership, toast]);

    // Get member names for the confirmation dialog
    const memberNamesToRemove = useMemo(() => {
        if (!orgMembers?.members) return [];
        return membersToRemove.map((id) => {
            const membership = memberships?.find((m) => m.subject?.id === id);
            const orgMember = orgMembers.members.find((m) => m.userId === membership?.subject?.id);
            return orgMember?.fullName || `Member ${id.slice(-6)}`;
        });
    }, [membersToRemove, memberships, orgMembers?.members]);

    const handleDeleteSuccess = useCallback(() => {
        navigate("/settings/members#groups");
    }, [navigate]);

    const isDeleting = deleteMembership.isPending;

    if (isLoadingGroups || isGroupMembershipsLoading || isLoadingOrgMembers) {
        return <LoadingState />;
    }

    if (!group) {
        return <GroupNotFound />;
    }

    if (group.systemManaged) {
        return <GroupIsSystemManagedWarning group={group} />;
    }

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="mb-6">
                {/* Title and Actions */}
                <div className="mb-4 flex items-start justify-between">
                    <div>
                        <p className="text-content-secondary">
                            This team will manage all projects and repos across our org.
                        </p>
                    </div>
                </div>
            </div>

            <GroupMembersTable
                memberships={memberships || []}
                orgMembers={orgMembers?.members || []}
                onRemoveMember={handleRemoveMembers}
                onAddPeopleClick={() => setShowAddMembersDialog(true)}
                canModifyMembers={canModifyMembers}
            />

            {/* Add Members Dialog */}
            <AddGroupMembersDialog
                open={showAddMembersDialog}
                onClose={() => setShowAddMembersDialog(false)}
                group={group}
            />

            {/* Remove Member Confirmation Dialog */}
            <RemoveMemberConfirmDialog
                open={showRemoveConfirmation}
                onOpenChange={(open) => {
                    if (!open) {
                        setMembersToRemove([]);
                        setShowRemoveConfirmation(false);
                    }
                }}
                memberNames={memberNamesToRemove}
                isLoading={isDeleting}
                onConfirm={handleConfirmRemove}
            />

            {/* Edit Group Dialog */}
            {group && (
                <EditGroupDialog
                    open={showEditGroupDialog}
                    onClose={() => setShowEditGroupDialog(false)}
                    group={group}
                />
            )}

            {/* Delete Group Dialog */}
            {group && (
                <DeleteGroupDialog
                    open={showDeleteGroupDialog}
                    onOpenChange={setShowDeleteGroupDialog}
                    group={group}
                    onSuccess={handleDeleteSuccess}
                />
            )}
        </div>
    );
};
